import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

export const TENANT_BANDS = ['1-4', '5-14', '15-49', '50+'] as const;
export type TenantBand = (typeof TENANT_BANDS)[number];

export const HEARD_FROM = [
  { value: 'Kaseya Connect', label: 'Kaseya Connect' },
  { value: 'LinkedIn', label: 'LinkedIn' },
  { value: 'Referral', label: 'A colleague or another MSP' },
  { value: 'Search', label: 'Search' },
  { value: 'Other', label: 'Other' },
] as const;

export interface SignupDetails {
  name: string;
  email: string;
  mobile: string;
  mspName: string;
  jobTitle: string;
  tenantBand: TenantBand | '';
  heardFrom: string;
  marketingOptIn: boolean;
}

export interface SignupState {
  details?: SignupDetails;
  /** Personalised link token (?t=): the sign-up attaches to that Pipedrive deal. */
  token?: string;
  /** Details were saved at the table (POST /api/capture). */
  captured?: boolean;
  /** Microsoft ID token from "Continue with Microsoft"; the API checks it. */
  msIdToken?: string;
  signupId?: string;
  signupFor?: string; // email the signupId was issued for
  serverError?: { field: string; message: string };
}

const STORE_KEY = 'tellec-nfr';

// sessionStorage can throw (private mode, blocked storage). The flow still
// works without it because the signup ID also travels in the URL.
function load(): SignupState {
  try {
    return JSON.parse(sessionStorage.getItem(STORE_KEY) ?? '{}') as SignupState;
  } catch {
    return {};
  }
}

function save(state: SignupState) {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

interface Ctx {
  state: SignupState;
  update: (patch: Partial<SignupState>) => SignupState;
  /** Forget everything: the next person at a shared tablet starts clean. */
  reset: () => void;
}

const SignupContext = createContext<Ctx | null>(null);

export function SignupProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SignupState>(load);
  const update = useCallback((patch: Partial<SignupState>) => {
    const next = { ...load(), ...patch };
    save(next);
    setState(next);
    return next;
  }, []);
  const reset = useCallback(() => {
    save({});
    setState({});
  }, []);
  return <SignupContext.Provider value={{ state, update, reset }}>{children}</SignupContext.Provider>;
}

export function useSignup(): Ctx {
  const ctx = useContext(SignupContext);
  if (!ctx) throw new Error('useSignup must be used inside SignupProvider');
  return ctx;
}

// Signup IDs are opaque URL-safe tokens issued by POST /api/signup.
export function validSignupId(id: string | null | undefined): string {
  return id && /^[A-Za-z0-9_-]{16,64}$/.test(id) ? id : '';
}

export function consentStartUrl(signupId: string): string {
  return `${window.location.origin}/api/consent/start?id=${encodeURIComponent(signupId)}`;
}
